import { faSquare, faSquareCheck } from "@fortawesome/pro-light-svg-icons";

import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    ListItem,
    Stack,
    TextField,
    useTheme,
} from "@vapor/react-material";
import {
    AdapterDateFns,
    DatePicker,
    LocalizationProvider,
} from "@vapor/react-x-date-pickers";
import it from "date-fns/locale/it";
import dayjs from "dayjs";
import {
    type ChangeEvent,
    type ReactElement,
    type SyntheticEvent,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
    ResponseStatus,
    useAddCustomFulfilment,
} from "../../../../../core/api/use-add-custom-fulfilment/useAddCustomFulfilment";
import { useGetFulfilmentTaxPayers } from "../../../../../core/api/use-get-fulfilment-tax-payers/useGetFulfilmentTaxPayers";
import type { FulfilmentDto } from "../../../../../core/api/use-get-fulfilments/FulfilmentDto";
import type { LinkableTaxPayerDto } from "../../../../../core/api/use-get-linkable-tax-payers/LinkableTaxPayerDto";
import { useGetLinkableTaxPayers } from "../../../../../core/api/use-get-linkable-tax-payers/useGetLinkableTaxPayers";
import { useUpdateCustomFulfilment } from "../../../../../core/api/use-update-custom-fulfilment/useUpdateCustomFulfilment";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { DialogWarning } from "../dialog-warning/DialogWarning";

interface AddOrEditCustomFulfilmentFormModel {
    linkableTaxPayers: LinkableTaxPayerDto[];
    deadline: Date;
    fulfilmentName: string;
}

interface AddOrEditCustomFulfilmentProps {
    fulfilment?: FulfilmentDto;
    mode: "add" | "edit";
    onAddOrEditCustomFulfilment: () => unknown;
    onClickCancel: () => unknown;
}
export const AddOrEditCustomFulfilment = ({
    fulfilment,
    mode,
    onAddOrEditCustomFulfilment,
    onClickCancel,
}: AddOrEditCustomFulfilmentProps): ReactElement => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();
    const theme = useTheme();

    const [openWarning, setOpenWarning] = useState(false);

    // Declarative query: auto-fetches in "add" mode (no params = fetch all)
    const { data: linkableTaxPayers, isFetching: isLoadingLinkableTaxPayers } =
        useGetLinkableTaxPayers(mode === "add" ? {} : undefined);

    // Declarative query: auto-fetches when in edit mode with tax payers
    const fulfilmentTaxPayersParams = useMemo(
        () =>
            mode === "edit" &&
            fulfilment?.taxPayerCounters &&
            fulfilment.taxPayerCounters.total > 0
                ? {
                      levyId: fulfilment.levyId || "",
                      year: dayjs(fulfilment.deadline).year(),
                  }
                : undefined,
        [fulfilment, mode],
    );
    const {
        data: getFulfilmentTaxPayersData,
        isFetching: getFulfilmentTaxPayersLoading,
    } = useGetFulfilmentTaxPayers(fulfilmentTaxPayersParams);

    const [formModel, setFormModel] =
        useState<AddOrEditCustomFulfilmentFormModel>({
            linkableTaxPayers: [],
            deadline: fulfilment ? new Date(fulfilment?.deadline) : new Date(),
            fulfilmentName: fulfilment?.description || "",
        });

    const addCustomFulfilmentMutation = useAddCustomFulfilment();
    const updateCustomFulfilmentMutation = useUpdateCustomFulfilment();

    const canAddOrEditCustomFulfilment = useMemo(() => {
        return !!formModel.deadline && !!formModel.fulfilmentName;
    }, [formModel.deadline, formModel.fulfilmentName]);

    const [prevTaxPayersData, setPrevTaxPayersData] = useState(
        getFulfilmentTaxPayersData,
    );

    // Seed the form's linkableTaxPayers with the fulfilment's currently-linked
    // tax payers when entering edit mode. Uses the "adjust state while
    // rendering" pattern (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
    // so the seed runs synchronously the first time the query lands, without
    // an extra render via useEffect.
    if (getFulfilmentTaxPayersData !== prevTaxPayersData) {
        setPrevTaxPayersData(getFulfilmentTaxPayersData);
        // Edit mode only: in add mode the selection comes from the user, not
        // from this query. Map unconditionally (empty list included) so
        // switching to a fulfilment without linked tax payers clears the
        // previously-seeded selection instead of leaving stale data in the
        // disabled autocomplete.
        if (mode === "edit") {
            setFormModel((prev) => ({
                ...prev,
                linkableTaxPayers: (getFulfilmentTaxPayersData ?? []).map(
                    (taxPayer) => ({
                        name: taxPayer.businessName,
                        workspaceId: taxPayer.workspaceId,
                        fiscalCode: "",
                        vatNumber: "",
                    }),
                ),
            }));
        }
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let response: ResponseStatus | null = null;

        try {
            response =
                mode === "add"
                    ? await addCustomFulfilmentMutation.mutateAsync({
                          deadline: formModel.deadline,
                          fulfilmentName: formModel.fulfilmentName,
                          workspaceIds: formModel.linkableTaxPayers.map(
                              (wp) => wp.workspaceId,
                          ),
                      })
                    : mode === "edit" && fulfilment
                      ? await updateCustomFulfilmentMutation.mutateAsync({
                            deadline: formModel.deadline,
                            fulfilmentName: formModel.fulfilmentName,
                            levyId: fulfilment.levyId,
                        })
                      : null;
        } catch {
            return;
        }

        if (response === ResponseStatus.OK && mode === "add") {
            showTextMessage(
                t(
                    "views.home.components.add_or_edit_custom_fulfilment.add.success",
                ),
            );
            onAddOrEditCustomFulfilment();
        } else {
            if (response === ResponseStatus.CONFLICT) {
                setOpenWarning(true);
            }
        }

        if (response === ResponseStatus.OK && mode === "edit") {
            showTextMessage(
                t(
                    "views.home.components.add_or_edit_custom_fulfilment.edit.success",
                ),
            );
            onAddOrEditCustomFulfilment();
        } else {
            if (response === ResponseStatus.CONFLICT) {
                setOpenWarning(true);
            }
        }
    };

    const handleOnConfirm = () => {
        setOpenWarning(false);
    };

    const handleOnDismiss = () => {
        if (onClickCancel) onClickCancel();
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack p={3}>
                <Stack justifyContent="space-between">
                    <Stack gap={1}>
                        <TextField
                            error={!formModel.fulfilmentName}
                            fullWidth
                            label={t(
                                "views.home.components.add_or_edit_custom_fulfilment.fulfilment.name",
                            )}
                            onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                            ) => {
                                setFormModel((prev) => {
                                    return {
                                        ...prev,
                                        fulfilmentName: event.target.value,
                                    };
                                });
                            }}
                            value={formModel.fulfilmentName}
                        />

                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={it}
                        >
                            <DatePicker
                                label={t(
                                    "views.home.components.add_or_edit_custom_fulfilment.fulfilment.deadline",
                                )}
                                onChange={(newValue) => {
                                    setFormModel((prev) => ({
                                        ...prev,
                                        deadline: dayjs(newValue as Date)
                                            .hour(12)
                                            .minute(0)
                                            .second(0)
                                            .millisecond(0)
                                            .toDate(),
                                    }));
                                }}
                                value={formModel.deadline}
                            />
                        </LocalizationProvider>
                        {/* FIXME: use autocomplete from vapor once fullwidth is fixed and render input's placeholder displays correctly on multiline */}
                        <Autocomplete
                            disabled={
                                mode === "edit" ||
                                (mode === "add" && isLoadingLinkableTaxPayers)
                            }
                            disableCloseOnSelect
                            filterOptions={
                                mode === "edit"
                                    ? undefined
                                    : mode === "add"
                                      ? (
                                            options: LinkableTaxPayerDto[],
                                            { inputValue },
                                        ) =>
                                            options.filter(
                                                (option) =>
                                                    option.fiscalCode
                                                        .toLowerCase()
                                                        .includes(
                                                            inputValue.toLowerCase(),
                                                        ) ||
                                                    option.name
                                                        .toLowerCase()
                                                        .includes(
                                                            inputValue.toLowerCase(),
                                                        ) ||
                                                    option.vatNumber
                                                        .toLowerCase()
                                                        .includes(
                                                            inputValue.toLowerCase(),
                                                        ),
                                            )
                                      : undefined
                            }
                            fullWidth
                            getOptionLabel={(option: LinkableTaxPayerDto) =>
                                option.name
                            }
                            isOptionEqualToValue={(option, value) => {
                                return option.workspaceId === value.workspaceId;
                            }}
                            multiple
                            noOptionsText={t(
                                "views.home.components.add_or_edit_custom_fulfilment.fulfilment.workspace_ids",
                            )}
                            onChange={(
                                _: SyntheticEvent<Element, Event>,
                                value: LinkableTaxPayerDto[],
                            ) => {
                                setFormModel((prev) => ({
                                    ...prev,
                                    linkableTaxPayers: value,
                                }));
                            }}
                            options={
                                mode === "add"
                                    ? linkableTaxPayers || []
                                    : mode === "edit"
                                      ? formModel.linkableTaxPayers
                                      : []
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t(
                                        "views.home.components.fulfilment_list_header.filters.taxPayer.label",
                                    )}
                                    placeholder={t(
                                        "views.home.components.fulfilment_list_header.filters.taxPayer.placeholder",
                                    )}
                                    InputLabelProps={{
                                        ...params.InputLabelProps,
                                        shrink:
                                            formModel.linkableTaxPayers
                                                ?.length === 0,
                                    }}
                                    // InputProps={{
                                    //     ...params.InputProps,
                                    //     sx: { width: "100%" },
                                    // }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment:
                                            getFulfilmentTaxPayersLoading ? (
                                                <CircularProgress
                                                    color="inherit"
                                                    sx={{ ml: 1 }}
                                                    size={16}
                                                />
                                            ) : null,
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            padding: 0,
                                            paddingLeft: "4px",
                                        },
                                    }}
                                />
                            )}
                            renderOption={(
                                props,
                                option: LinkableTaxPayerDto,
                                { selected },
                            ) => (
                                <ListItem {...props} key={option.workspaceId}>
                                    <Stack direction="row">
                                        <Stack>
                                            <Checkbox
                                                icon={
                                                    <VaporIcon
                                                        icon={faSquare}
                                                        color={
                                                            theme.palette
                                                                .primary.cadet
                                                        }
                                                    />
                                                }
                                                checkedIcon={
                                                    <VaporIcon
                                                        icon={faSquareCheck}
                                                        color={
                                                            theme.palette
                                                                .primary.cadet
                                                        }
                                                    />
                                                }
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                        </Stack>
                                        <Stack>
                                            {option.name && (
                                                <Typography>
                                                    {option.name}
                                                </Typography>
                                            )}
                                            {option.vatNumber && (
                                                <Typography
                                                    color={
                                                        theme.palette.primary
                                                            .textSubduedColor
                                                    }
                                                >
                                                    {option.vatNumber}
                                                </Typography>
                                            )}
                                            {option.fiscalCode && (
                                                <Typography
                                                    color={
                                                        theme.palette.primary
                                                            .textSubduedColor
                                                    }
                                                >
                                                    {option.fiscalCode}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </ListItem>
                            )}
                            value={formModel.linkableTaxPayers}
                        />
                    </Stack>
                    <Box
                        sx={{
                            ml: -3,
                        }}
                    >
                        <VaporToolbar
                            contentLeft={[
                                <Button
                                    disabled={
                                        addCustomFulfilmentMutation.isPending ||
                                        updateCustomFulfilmentMutation.isPending
                                    }
                                    onClick={onClickCancel}
                                    variant="outlined"
                                >
                                    {t(
                                        "views.home.components.add_or_edit_custom_fulfilment.buttons.cancel",
                                    )}
                                </Button>,
                            ]}
                            contentRight={[
                                <Button
                                    disabled={
                                        !canAddOrEditCustomFulfilment ||
                                        addCustomFulfilmentMutation.isPending ||
                                        updateCustomFulfilmentMutation.isPending
                                    }
                                    type="submit"
                                    variant="contained"
                                >
                                    {t(
                                        "views.home.components.add_or_edit_custom_fulfilment.buttons.submit",
                                    )}
                                    {(addCustomFulfilmentMutation.isPending ||
                                        updateCustomFulfilmentMutation.isPending) && (
                                        <CircularProgress
                                            color="inherit"
                                            sx={{ ml: 1 }}
                                            size={16}
                                        />
                                    )}
                                </Button>,
                            ]}
                            size="medium"
                            variant="regular"
                        />
                    </Box>
                    {openWarning && (
                        <DialogWarning
                            dialogContentText={t(
                                "views.home.components.add_or_edit_custom_fulfilment_warning.existingCustomFulfilment.dialogContent",
                            )}
                            dialogPrimaryButtonText={t(
                                "views.home.components.add_or_edit_custom_fulfilment_warning.existingCustomFulfilment.edit",
                            )}
                            dialogSecondaryButtonText={t(
                                "views.home.components.add_or_edit_custom_fulfilment_warning.existingCustomFulfilment.cancel",
                            )}
                            dialogTitleText={t(
                                "views.home.components.add_or_edit_custom_fulfilment_warning.existingCustomFulfilment.dialogTitle",
                            )}
                            onConfirm={handleOnConfirm}
                            onDismiss={handleOnDismiss}
                        ></DialogWarning>
                    )}
                </Stack>
            </Stack>
        </form>
    );
};
